// lossless.ts — Tier-3 lossless playback in the browser (PROTOCOL §1.2 Tier 3).
//
// Parallel to the LiveKit/Opus path: a WebSocket to the server's /lossless
// endpoint delivers bit-exact PCM frames (our `DAWL` wire format), which we
// convert to Float32 and feed to an AudioWorklet that renders them to the
// speakers. Modelled on a production lossless web player (Audiomovers ListenTo):
// WebSocket transport, an AudioWorklet fed via postMessage with transferables,
// AudioContext pinned to 48 kHz, and NO SharedArrayBuffer — so no COOP/COEP /
// cross-origin-isolation requirement on the host.
//
// The Opus path keeps running (muted) as the resilient floor: any WS / decode /
// support failure falls back to it via `onFallback`.
//
// PCM (format 0) is decoded inline; FLAC (format 1, the bandwidth tier) is decoded
// with @wasm-audio-decoders/flac. The decoder is imported lazily (dynamic import)
// so it's a separate Vite chunk and never loaded by the node unit tests.

// Type-only imports (erased at compile time → no runtime/test load of the packages).
import type { FLACDecoder } from "@wasm-audio-decoders/flac";
import type { OpusDecoder } from "opus-decoder";

/** Internal Opus rate; the lossless AudioContext is pinned here to keep the
 *  streamSamples↔seconds slope exact (a 44.1 kHz context would skew it ~8.8%). */
export const LOSSLESS_SAMPLE_RATE = 48_000;

/** Default per-receiver automatic prebuffer (not end-to-end latency). */
export const LOSSLESS_DEFAULT_TARGET_MS = 100;

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

/** Planar Float32 channels → interleaved Float32 for the worklet ring. */
function interleave(channels: Float32Array[], frames: number): Float32Array {
  const ch = channels.length;
  const out = new Float32Array(frames * ch);
  for (let c = 0; c < ch; c++) {
    const src = channels[c]!;
    for (let f = 0; f < frames; f++) out[f * ch + c] = src[f]!;
  }
  return out;
}

/** Parsed `DAWL` wire header (see server/src/lossless.rs `build_frame`). */
export interface LosslessHeader {
  version: number;
  format: number; // 0 = PCM s16, 1 = FLAC
  channels: number;
  bitDepth: number;
  sampleRate: number;
  seq: number;
  streamSamples: number;
}

const HEADER_BYTES = 28;
// "DAWL"
const M0 = 0x44, M1 = 0x41, M2 = 0x57, M3 = 0x4c;

/** Parse a `/lossless` binary frame: 28-byte LE header + payload. Returns null
 *  if the buffer is too short or the magic doesn't match. */
export function parseLosslessFrame(
  buf: ArrayBuffer,
): { header: LosslessHeader; payload: Uint8Array } | null {
  if (buf.byteLength < HEADER_BYTES) return null;
  const dv = new DataView(buf);
  if (dv.getUint8(0) !== M0 || dv.getUint8(1) !== M1 || dv.getUint8(2) !== M2 || dv.getUint8(3) !== M3) {
    return null;
  }
  const header: LosslessHeader = {
    version: dv.getUint8(4),
    format: dv.getUint8(5),
    channels: dv.getUint8(6),
    bitDepth: dv.getUint8(7),
    sampleRate: dv.getUint32(8, true),
    seq: Number(dv.getBigUint64(12, true)),
    streamSamples: Number(dv.getBigUint64(20, true)),
  };
  return { header, payload: new Uint8Array(buf, HEADER_BYTES) };
}

/** Convert interleaved integer PCM (16/24-bit LE) or 32-bit float to interleaved
 *  Float32 in [-1, 1). Returns the typed array whose `.buffer` is transferable. */
export function pcmToFloat32(payload: Uint8Array, bitDepth: number): Float32Array {
  if (bitDepth === 16) {
    const n = payload.byteLength >> 1;
    const out = new Float32Array(n);
    const dv = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
    for (let i = 0; i < n; i++) out[i] = dv.getInt16(i * 2, true) / 32768;
    return out;
  }
  if (bitDepth === 24) {
    const n = (payload.byteLength / 3) | 0;
    const out = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const b0 = payload[i * 3]!, b1 = payload[i * 3 + 1]!, b2 = payload[i * 3 + 2]!;
      let v = b0 | (b1 << 8) | (b2 << 16);
      if (v & 0x800000) v |= ~0xffffff; // sign-extend 24→32
      out[i] = v / 8388608;
    }
    return out;
  }
  // 32-bit float passthrough.
  const n = payload.byteLength >> 2;
  const out = new Float32Array(n);
  const dv = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  for (let i = 0; i < n; i++) out[i] = dv.getFloat32(i * 4, true);
  return out;
}

// AudioWorklet processor: an interleaved-sample ring buffer drained at the
// context rate. Prebuffers to a target depth before starting (and re-prebuffers
// after an underrun) so transient WS jitter doesn't glitch. Reports its depth
// back so the main thread can estimate the audible position for timecode.
export const WORKLET_CODE = `
class LosslessProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.ch = 2;
    this.cap = sampleRate * 2 * 4;     // ~4 s of stereo headroom (interleaved floats)
    this.ring = new Float32Array(this.cap);
    this.positions = new Float64Array(this.cap / 2);
    this.audibleSample = null;
    this.automatic = true;
    this.sourcePlaying = false;
    this.lastFailure = 0;
    this.lastGrowth = -Infinity;
    this.elapsed = 0;
    this.r = 0; this.w = 0; this.count = 0;
    this.target = Math.floor(sampleRate * 0.1) * this.ch; // 100 ms prebuffer (pre-config)
    this.started = false;
    this.underruns = 0;
    this.lastPeak = 0;   // peak of the most recent output block (silence gate)
    this.tmp = new Float32Array(8);
    this.tick = 0;
    this.port.onmessage = (e) => {
      const d = e.data;
      if (d.type === 'config') {
        this.ch = (d.channels | 0) || 2;
        this.target = Math.max(1, Math.floor((d.targetMs || 100) / 1000 * sampleRate)) * this.ch;
        this.automatic = d.automatic !== false;
        // Configuration changes never cut music. Apply at the next refill/reset.
      } else if (d.type === 'source') {
        if (!d.playing && this.sourcePlaying && this.automatic &&
            this.elapsed - this.lastFailure > 60) {
          this.target = Math.max(sampleRate * 0.1 * this.ch,
                                this.target - sampleRate * 0.125 * this.ch);
        }
        this.sourcePlaying = !!d.playing;
      } else if (d.type === 'samples') {
        const s = d.samples;
        // SURPLUS SHEDDING: standing depth above target is standing LATENCY,
        // and it only ever grows (every DAW play/seek burst adds a slice the
        // sender cannot take back). Shed it inaudibly: while over target, drop
        // incoming frames that are near-silence (the plugin streams true
        // silence during pauses/stops, so every break re-converges the ring
        // to the receiver target without ever cutting audible material).
        if (this.count > this.target + sampleRate * 0.05 * this.ch) {
          let peak = 0;
          for (let i = 0; i < s.length; i++) { const a = s[i] < 0 ? -s[i] : s[i]; if (a > peak) peak = a; }
          if (peak < 3e-4) return; // ~-70 dBFS: codec-noise silence, not music
        }
        for (let i = 0; i < s.length; i++) {
          if (this.count >= this.cap) { this.r = (this.r + 1) % this.cap; this.count--; } // overflow: drop oldest
          if (i % this.ch === 0)
            this.positions[this.w / this.ch] = d.streamSamples - (s.length - i) / this.ch;
          this.ring[this.w] = s[i]; this.w = (this.w + 1) % this.cap; this.count++;
        }
      } else if (d.type === 'reset') {
        this.r = this.w = this.count = 0; this.started = false; this.audibleSample = null;
      }
    };
  }
  process(_inputs, outputs) {
    const out = outputs[0];
    if (!out || out.length === 0) return true;
    const outCh = out.length, frames = out[0].length;
    this.elapsed += frames / sampleRate;
    if (!this.started) {
      if (this.count >= this.target) {
        // REVIEW 2.2: start AT the target depth, never at whatever piled up
        // while the context was suspended. Safari auto-join without a gesture
        // (or an iOS interruption) leaves the socket open while playback is
        // refused: frames fill the ring to its 4 s cap, and starting from
        // there parked up to ~3.5 s of STANDING latency for minutes — the
        // surplus shedding only removes silence, so music kept it forever,
        // and the readout ran seconds ahead of what was heard. Same
        // channel-aligned skip-forward the config handler already does.
        if (this.count > this.target) {
          let drop = this.count - this.target;
          drop -= drop % this.ch;
          this.r = (this.r + drop) % this.cap;
          this.count -= drop;
        }
        this.started = true;
      } else { for (let c = 0; c < outCh; c++) out[c].fill(0); return true; }
    }
    let underflow = false;
    let blockPeak = 0;
    for (let f = 0; f < frames; f++) {
      if (this.count >= this.ch) {
        this.audibleSample = this.positions[this.r / this.ch];
        for (let c = 0; c < this.ch; c++) { this.tmp[c] = this.ring[this.r]; this.r = (this.r + 1) % this.cap; this.count--; }
        for (let c = 0; c < outCh; c++) {
          const v = this.tmp[c < this.ch ? c : this.ch - 1];
          out[c][f] = v;
          const a = v < 0 ? -v : v; if (a > blockPeak) blockPeak = a;
        }
      } else {
        for (let c = 0; c < outCh; c++) out[c][f] = 0;
        underflow = true;
      }
    }
    if (underflow) {
      // Grow only for a genuinely audible underrun while the DAW is playing.
      // A suspended AudioContext does not run process(), so cannot grow the buffer.
      if (this.sourcePlaying && Math.max(this.lastPeak, blockPeak) >= 3e-4) {
        this.underruns++;
        this.lastFailure = this.elapsed;
        if (this.automatic && this.elapsed - this.lastGrowth >= 2) {
          this.target = Math.min(sampleRate * 1.5 * this.ch,
            this.target + Math.max(sampleRate * 0.125 * this.ch, this.target * 0.5));
          this.lastGrowth = this.elapsed;
        }
      }
      this.started = false;
    }
    if (!underflow) this.lastPeak = blockPeak;
    if (underflow || (this.tick++ & 15) === 0) {
      this.port.postMessage({ type: 'stat', depthFrames: (this.count / this.ch) | 0,
        renderedAt: currentTime + frames / sampleRate, rendering: !underflow, audibleSample: this.audibleSample, underruns: this.underruns, targetMs: Math.round(this.target / this.ch / sampleRate * 1000) });
    }
    return true;
  }
}
registerProcessor('lossless-processor', LosslessProcessor);
`;

type Ctor = {
  sessionId: string;
  token: string;
  /** Initial receiver-local buffer depth in ms. */
  targetMs?: number;
  onError?: (msg: string) => void;
  /** Lossless became the audible output (host is sending it + it prebuffered).
   *  The UI crossfades from Opus to lossless. */
  onActive?: () => void;
  /** Lossless is no longer the output (host on Opus / stream stopped / connection
   *  too slow / error). The UI crossfades back to Opus. */
  onInactive?: (reason: string) => void;
};

type SinkCapableContext = AudioContext & { setSinkId?: (id: string) => Promise<void> };

/** Browser-side lossless player. One instance per live session. `enable()` builds
 *  the graph + WebSocket (call it from a user gesture so the AudioContext may
 *  start); a watchdog then drives onActive/onInactive automatically as the host's
 *  lossless frames flow / stop / the connection degrades — no manual toggle. */
export class LosslessPlayer {
  private ctx: SinkCapableContext | null = null;
  private node: AudioWorkletNode | null = null;
  private gain: GainNode | null = null;
  private ws: WebSocket | null = null;
  /** FLAC decoder (lazy-created in enable()); null for the PCM path / before ready. */
  private flac: FLACDecoder | null = null;
  /** Serializes FLAC decode() calls — it's stateful; concurrent calls desync. */
  private flacChain: Promise<void> = Promise.resolve();
  /** Pending auto-reconnect timer — cleared on disable() so a torn-down player
   *  can never resurrect a socket. */
  private reconnectTimer: number | null = null;
  /** Generation latch: disable() bumps it; an enable() still in flight after
   *  an await checks it and aborts instead of finishing a zombie graph. */
  private gen = 0;
  /** Opus decoder (§11 WS-Opus primary path; lazy-created in enable()). */
  private opus: OpusDecoder | null = null;
  /** When true-lossless frames (FLAC/PCM) flowed recently, §11 Opus frames on
   *  the same socket are skipped so the ring is fed by exactly one source. */
  private lastTrueLosslessMs = 0;
  /** The graph + WebSocket are up (whether or not lossless is currently audible). */
  private enabled = false;
  /** Lossless is currently the audible output (frames flowing + prebuffered). */
  private heard = false;
  private volume = 1;
  private muted = false;
  private sinkId: string | null = null;

  // Watchdog state: drives the automatic active/inactive transitions.
  private watchdog: number | null = null;
  /** Reconnect backoff (ms). Reset on a successful open; grows to 30 s so an
   *  expired token / dead server is not hammered every second forever. */
  private reconnectDelayMs = 1000;
  private lastFrameMs = 0;
  /** When kick() last ran — a second kick with frames still missing means the
   *  socket is half-open rather than merely idle (REVIEW 2.2). */
  private lastKickAtMs = 0;
  /** No frames for this long WITH the mix live = the socket is not just idle. */
  private static readonly STALE_SOCKET_MS = 3000;
  /** How long one kick is remembered for the escalation above. */
  private static readonly KICK_MEMORY_MS = 30_000;
  private lastStatMs = 0;
  /** Don't reactivate before this (set after a drain-triggered fallback; anti-flap). */
  private cooldownUntilMs = 0;

  /** Ring depth (samples-per-channel) reported by the worklet; drives activation. */
  private depthFrames = 0;
  /** Cumulative worklet underruns (from stat messages) + the health baseline. */
  private statUnderruns = 0;
  private healthBaseline = 0;
  /** Latest frame's PCM bit depth — for the format caption. */
  private lastBitDepth = 0;

  private readonly o: Ctor;
  /** Receiver-local prebuffer target, independent of the host. */
  private targetMs: number;
  private automatic = true;
  private audibleSample: number | null = null;
  private renderedAt = 0;
  private rendering = false;

  getTargetMs(): number { return this.targetMs; }
  getDepthMs(): number { return this.depthFrames / LOSSLESS_SAMPLE_RATE * 1000; }
  getAudibleSample(): number | null {
    if (!this.isActive() || !this.isCtxRunning() || this.audibleSample === null) return null;
    const age = this.rendering ? Math.min(50, Math.max(0, ((this.ctx?.currentTime ?? 0) - this.renderedAt) * 1000)) : 0;
    const outputMs = ((this.ctx?.baseLatency ?? 0) + (this.ctx?.outputLatency ?? 0)) * 1000;
    return this.audibleSample + (age - outputMs) * 48;
  }
  setSourcePlaying(playing: boolean): void {
    this.node?.port.postMessage({ type: "source", playing });
  }
  setBufferMode(ms: number | null): void {
    this.automatic = ms === null;
    this.setTargetMs(ms ?? LOSSLESS_DEFAULT_TARGET_MS);
  }

  constructor(opts: Ctor) {
    this.o = opts;
    this.targetMs = opts.targetMs ?? LOSSLESS_DEFAULT_TARGET_MS;
  }

  /** Set the ring prebuffer depth (ms). Takes effect on the NEXT enable() (the
   *  initial prebuffer); a live update only changes post-underrun re-prebuffering,
   *  so callers set this BEFORE enabling to actually move the steady-state latency. */
  setTargetMs(ms: number): void {
    this.targetMs = Math.min(2000, Math.max(50, Math.round(ms)));
    this.node?.port.postMessage({ type: "config", channels: 2, targetMs: this.targetMs, automatic: this.automatic });
  }

  /** True if this browser has the APIs the lossless path needs. */
  static isSupported(): boolean {
    return (
      typeof window !== "undefined" &&
      typeof AudioWorkletNode !== "undefined" &&
      (typeof window.AudioContext !== "undefined" ||
        typeof (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext !== "undefined") &&
      typeof WebSocket !== "undefined"
    );
  }

  /** The player's AudioContext — the ONE context on the page that is
   *  guaranteed to be unlocked and running whenever the stream is audible.
   *  The room renders the call voices through it so they can never end up on
   *  a suspended context of their own (iOS one-way audio). */
  /** How many channels the OUTPUT can carry. The iOS tell: a context born
   *  while the device is still in the voice-processing session reports a MONO
   *  destination, and everything it plays stays mono until it is rebuilt. This
   *  is the difference between "we rebuilt" and "we rebuilt in time" — and it
   *  is measurable, which every previous attempt at this was not. 0 = no
   *  context. */
  outputChannels(): number {
    return this.ctx?.destination.maxChannelCount ?? 0;
  }

  getContext(): AudioContext | null {
    return this.ctx;
  }

  /** True while lossless is the audible output. */
  isActive(): boolean {
    return this.heard;
  }

  /** The WS graph is built and considered the audible path, but its context is
   *  NOT rendering (autoplay policy before the first gesture, or an iOS
   *  interruption). In that state nothing is heard on this path — the caller
   *  must un-suppress the WebRTC floor and offer a tap-to-play. */
  isEnabledButSilent(): boolean {
    return this.enabled && this.ctx !== null && this.ctx.state !== "running";
  }

  /** Connection-health level of THIS (audible) path since the last call:
   *  0 good / 1 marginal / 2 poor, from worklet ring underruns — each one is a
   *  REAL audible interruption (the ring re-prebuffers). Poll ~every 5 s. */
  healthLevel(): number {
    const delta = Math.max(0, this.statUnderruns - this.healthBaseline);
    this.healthBaseline = this.statUnderruns;
    return delta >= 2 ? 2 : delta >= 1 ? 1 : 0;
  }

  /** True while the player still needs a user gesture to actually run: Safari
   *  refuses AudioContext.resume() outside a gesture call stack, so the graph
   *  can sit built-but-suspended (data flowing, nothing audible). The UI keeps
   *  its gesture hooks alive until this turns false. */
  needsGesture(): boolean {
    return !(this.enabled && this.ctx !== null && this.ctx.state === "running");
  }

  /** Resume the AudioContext if a proactive build left it suspended (autoplay
   *  policy needs a user gesture). Safe to call from any gesture, repeatedly. */
  resume(): void {
    // iOS reports "interrupted" (non-standard) after screen-off/route changes —
    // resume ANY non-running state, not just the spec'd "suspended".
    if (this.ctx && this.ctx.state !== "running") void this.ctx.resume().catch(() => {});
  }

  /** True while the context exists and renders. iOS audio-session flips (mic
   *  open/close) silently interrupt it — the UI watchdog polls this. */
  isCtxRunning(): boolean {
    return this.ctx !== null && this.ctx.state === "running";
  }

  /** Milliseconds since the last frame arrived (Infinity before the first). */
  framesStaleMs(): number {
    return this.lastFrameMs ? now() - this.lastFrameMs : Infinity;
  }

  /** The stream is (back) — reconnect NOW instead of waiting out a backoff.
   *  After a host stop the socket may have retried its way up to a 30 s delay;
   *  a listener then sat in silence for up to half a minute after the host had
   *  already restarted. Also used by the stall watchdog. */
  kick(): void {
    this.reconnectDelayMs = 1000; // forget the long backoff
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (!this.enabled) return;
    if (!this.ws) {
      this.connectWs();
      return;
    }
    // REVIEW 2.2: a HALF-OPEN socket (NAT rebind, Wi-Fi switch, server gone
    // without FIN/RST) stays `OPEN` for the client while nothing arrives —
    // onclose only fires after the TCP timeout, i.e. minutes. Until then this
    // method did nothing useful: the socket exists, so no reconnect; and
    // resync() is a no-op because the ring ran dry long ago. The listener sat
    // on the WebRTC floor — on WebKit without the host's buffer at all.
    // Closing it ourselves fires onclose immediately and starts the reconnect.
    //
    // But NOT on the first kick: when the host resumes streaming, frames are
    // legitimately ancient (nothing was sent while they were stopped) and the
    // socket is perfectly healthy — killing it there would delay the very
    // recovery this call exists for. So the first kick just resyncs; only a
    // SECOND kick with frames still missing treats the socket as dead.
    const staleMs = this.framesStaleMs();
    const kickedBefore =
      this.lastKickAtMs > 0 && now() - this.lastKickAtMs < LosslessPlayer.KICK_MEMORY_MS;
    this.lastKickAtMs = now();
    // AND only a socket that has ALREADY delivered frames may be treated as
    // half-open. Field report 19.08. ("mono, even without the call"): on a
    // FRESH connection lastFrameMs is 0, so framesStaleMs() is Infinity — two
    // kicks a second apart therefore closed every new socket about two
    // seconds after opening it, before the first frame could arrive. The
    // player never came up, listeners fell back to the WebRTC floor, and on
    // iOS that floor is mono: "phone call quality". A socket that has never
    // delivered is the reconnect logic's business, not this one's.
    if (
      this.lastFrameMs > 0 &&
      kickedBefore &&
      staleMs > LosslessPlayer.STALE_SOCKET_MS &&
      this.ws.readyState === WebSocket.OPEN
    ) {
      try {
        this.ws.close(); // fires onclose locally -> the reconnect path takes over
      } catch {
        /* already closing */
      }
      return;
    }
    this.resync();
  }

  /** Flush the worklet ring and re-prebuffer to the host target. Used on the
   *  DAW play edge: REAPER's prefill burst then FILLS the buffer instead of
   *  stacking on top of it as standing latency. */
  resync(): void {
    if (this.isActive()) this.node?.port.postMessage({ type: "reset" });
  }

  /** Latest PCM bit depth (24 for the bit-exact tap) — for the format caption. */
  bitDepth(): number {
    return this.lastBitDepth;
  }

  /** Build the audio graph + open the WebSocket and start the watchdog. Resolves
   *  once the socket is open; rejects (and tears down) on any failure so the caller
   *  can stay on Opus. Lossless only becomes AUDIBLE once frames flow (onActive). */
  async enable(): Promise<void> {
    if (this.enabled) return;
    if (!LosslessPlayer.isSupported()) throw new Error("lossless not supported in this browser");
    const myGen = ++this.gen;
    try {
      // FLAC + Opus decoders (lazy chunks). Ready before the socket opens so the
      // first payloads can decode immediately. Cheap WASM; one each per session.
      const [{ FLACDecoder }, { OpusDecoder }] = await Promise.all([
        import("@wasm-audio-decoders/flac"),
        import("opus-decoder"),
      ]);
      this.flac = new FLACDecoder();
      this.opus = new OpusDecoder();
      await Promise.all([this.flac.ready, this.opus.ready]);
      if (myGen !== this.gen) throw new Error("superseded"); // disable() raced us

      const Ctx: typeof AudioContext =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      // "interactive" (not "playback") minimises the AudioContext output buffer —
      // our own ring provides the stability margin, so we don't want the browser
      // adding a big playback buffer on top (that was ~tens of ms of extra latency).
      const ctx = new Ctx({
        sampleRate: LOSSLESS_SAMPLE_RATE,
        latencyHint: "interactive",
      }) as SinkCapableContext;
      this.ctx = ctx;

      const blob = new Blob([WORKLET_CODE], { type: "application/javascript" });
      const url = URL.createObjectURL(blob);
      try {
        await ctx.audioWorklet.addModule(url);
      } finally {
        URL.revokeObjectURL(url);
      }

      const node = new AudioWorkletNode(ctx, "lossless-processor", {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [2],
      });
      node.port.onmessage = (e: MessageEvent) => {
        const d = e.data as { type?: string; depthFrames?: number; underruns?: number; targetMs?: number; audibleSample?: number; rendering?: boolean; renderedAt?: number };
        if (d.type === "stat") {
          this.depthFrames = d.depthFrames ?? 0;
          this.targetMs = d.targetMs ?? this.targetMs;
          this.audibleSample = Number.isFinite(d.audibleSample) ? d.audibleSample! : null;
          this.renderedAt = d.renderedAt ?? this.ctx?.currentTime ?? 0;
          this.rendering = d.rendering === true;
          this.statUnderruns = d.underruns ?? 0;
          this.lastStatMs = now(); // a stat means the worklet has started (prebuffered)
        }
      };
      node.port.postMessage({ type: "config", channels: 2, targetMs: this.targetMs, automatic: this.automatic });

      const gain = ctx.createGain();
      gain.gain.value = 0; // silent start; the caller crossfades in once audio flows
      node.connect(gain).connect(ctx.destination);
      this.node = node;
      this.gain = gain;

      if (this.sinkId) await this.applySink(this.sinkId);
      if (myGen !== this.gen) throw new Error("superseded");
      if (ctx.state === "suspended") await ctx.resume().catch(() => {});
      // REVIEW 2.2: the latch was only checked after the decoder load, so a
      // disable() during applySink / ctx.resume() (teardownLive, reconnect,
      // pipeline rebuild) let this continuation run on an instance the UI had
      // already dropped: it opened a FRESH socket, set enabled and started the
      // watchdog. Result for the lifetime of the page — an orphaned /lossless
      // socket pulling the full stream (24-bit PCM ≈ 2.3 Mbit/s) into a null
      // node, plus an interval nobody can stop, and the next join opening a
      // second socket beside it. On Chrome enable() parks in ctx.resume()
      // without a gesture, which makes this the NORMAL path, not a rare race.
      if (myGen !== this.gen) throw new Error("superseded");

      await this.openSocket();
      if (myGen !== this.gen) throw new Error("superseded");
      this.enabled = true;
      // Watchdog: flip to/from audible as the host's frames flow / stop / degrade.
      this.lastFrameMs = 0;
      this.lastStatMs = 0;
      this.watchdog = window.setInterval(() => this.tickWatchdog(), 100);
    } catch (e) {
      // A "superseded" abort means a NEWER enable() owns this instance now —
      // tearing down here would kill its socket and context instead of ours.
      const superseded = e instanceof Error && e.message === "superseded";
      if (!superseded) await this.disable();
      throw e instanceof Error ? e : new Error(String(e));
    }
  }

  /** Drive the automatic active/inactive transitions (every 100 ms). */
  private tickWatchdog(): void {
    if (!this.enabled) return;
    const t = now();
    const frameAge = this.lastFrameMs ? t - this.lastFrameMs : Infinity;
    const ringMs = (this.depthFrames / LOSSLESS_SAMPLE_RATE) * 1000;
    const workletReady = t - this.lastStatMs < 400; // worklet started (prebuffered)

    if (!this.heard) {
      // Activate only when frames flow AND the ring has prebuffered to a healthy
      // depth — never crossfade INTO an immediate underrun. (0.6× target for
      // hysteresis against the drain threshold below.)
      const healthy = frameAge < 300 && workletReady && ringMs > this.targetMs * 0.6;
      if (healthy && t >= this.cooldownUntilMs) {
        this.heard = true;
        this.o.onActive?.();
      }
      return;
    }
    // Let the worklet bridge short outages and refill its adaptive buffer.
    // Resetting at a shallow ring would destroy the very backlog it needs.
    // A sustained stop/disconnect still hands control to the WebRTC fallback.
    if (frameAge > 2000 || (this.isCtxRunning() && t - this.lastStatMs > 3000)) {
      this.cooldownUntilMs = t + 2000; // brief anti-flap; re-prebuffer also gates re-entry
      this.goInactive("stream stopped");
    }
  }

  private goInactive(reason: string): void {
    this.heard = false;
    this.audibleSample = null;
    this.rendering = false;
    this.node?.port.postMessage({ type: "reset" }); // re-prebuffer when frames return
    this.o.onInactive?.(reason);
  }

  private openSocket(): Promise<void> {
    return new Promise<void>((resolve, reject) => this.connectWs(resolve, reject));
  }

  /** Open the /lossless WebSocket. On a mid-stream drop (e.g. a server restart or
   *  an ingest reconnect) it AUTO-RECONNECTS, keeping the audio graph alive — the
   *  server replays the FLAC prologue on the new subscription so the decoder
   *  re-inits. (Previously a drop disabled the player forever, so it stopped
   *  reflecting plugin changes until a page reload.) */
  private connectWs(onOpen?: () => void, onFail?: (e: Error) => void): void {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${location.host}/lossless?session=${encodeURIComponent(
      this.o.sessionId,
    )}&token=${encodeURIComponent(this.o.token)}`;
    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    let opened = false;
    ws.onopen = () => {
      opened = true;
      this.reconnectDelayMs = 1000; // healthy again — reset the backoff
      onOpen?.();
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data !== "string") this.onFrame(ev.data as ArrayBuffer);
    };
    ws.onerror = () => {
      if (!opened) onFail?.(new Error("lossless socket error"));
    };
    ws.onclose = () => {
      this.ws = null;
      if (!this.enabled) return; // intentional teardown
      if (!opened) {
        // Never reached open. On the INITIAL connect (enable() passed onFail) give
        // up so the caller stays on Opus. On a watchdog/auto reconnect (no onFail)
        // the server is merely down for a moment (e.g. a redeploy) — keep retrying,
        // otherwise a single failed retry disabled lossless until a page reload.
        if (onFail) {
          onFail(new Error("lossless socket closed before open"));
          return;
        }
        this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 1.7, 30_000);
        this.reconnectTimer = window.setTimeout(() => {
          this.reconnectTimer = null;
          if (this.enabled) this.connectWs();
        }, this.reconnectDelayMs);
        return;
      }
      // Dropped mid-stream: crossfade to Opus, reset the decoders, reconnect.
      if (this.heard) this.goInactive("lossless connection dropped");
      // FLAC decoder is stateful and all decode work is serialized on
      // flacChain — the reset must ride the same chain, not race a decode.
      this.flacChain = this.flacChain
        .then(() => this.flac?.reset())
        .catch(() => {})
        .then(() => undefined);
      void this.opus?.reset().catch(() => {});
      this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 1.7, 30_000);
      this.reconnectTimer = window.setTimeout(() => {
        this.reconnectTimer = null;
        if (this.enabled) this.connectWs();
      }, this.reconnectDelayMs);
    };
  }

  private onFrame(buf: ArrayBuffer): void {
    const parsed = parseLosslessFrame(buf);
    if (!parsed || !this.node) return;
    const { header, payload } = parsed;

    if (header.format === 2) {
      // §11 Opus over the WS (the always-on primary path). Skipped while true
      // lossless (FLAC/PCM) flows so the ring is fed by exactly one source; when
      // the host leaves lossless, Opus takes over after a short gap (the ring
      // re-prebuffers via the watchdog).
      if (now() - this.lastTrueLosslessMs < 1000) return;
      if (!this.opus) return;
      this.lastFrameMs = now();
      this.lastBitDepth = 0; // n/a → the UI labels this path "Opus"
      try {
        const { channelData, samplesDecoded } = this.opus.decodeFrame(payload);
        if (samplesDecoded > 0 && this.node) {
          const samples = interleave(channelData, samplesDecoded);
          this.node.port.postMessage({ type: "samples", samples, streamSamples: header.streamSamples }, [samples.buffer]);
        }
      } catch {
        /* skip an undecodable packet; a sustained gap falls back via the watchdog */
      }
      return;
    }

    this.lastFrameMs = now(); // watchdog: the host is actively sending lossless
    this.lastTrueLosslessMs = this.lastFrameMs;
    this.lastBitDepth = header.bitDepth;

    if (header.format === 1) {
      // FLAC: decode off the wire order (serialized). The first payload (the
      // "fLaC"+STREAMINFO prologue) inits the decoder; frames follow.
      this.decodeFlac(payload, header.streamSamples);
      return;
    }
    // format 0 = raw integer PCM — decode inline + transfer to the worklet ring.
    const samples = pcmToFloat32(payload, header.bitDepth);
    this.node.port.postMessage({ type: "samples", samples, streamSamples: header.streamSamples }, [samples.buffer]);
  }

  /** Decode one FLAC payload and feed the ring. Serialized through flacChain —
   *  decode() is stateful, so concurrent calls would desync the bitstream and
   *  break the wire order the streamSamples axis assumes. */
  private decodeFlac(payload: Uint8Array, streamSamples: number): void {
    this.flacChain = this.flacChain
      .then(async () => {
        if (!this.flac || !this.node) return;
        const { channelData, samplesDecoded } = await this.flac.decode(payload);
        if (samplesDecoded === 0) return; // prologue / partial frame buffered
        const samples = interleave(channelData, samplesDecoded);
        this.node.port.postMessage({ type: "samples", samples, streamSamples }, [samples.buffer]);
      })
      .catch(() => {
        // Keep the chain alive: decode() self-syncs on the next frame, and a
        // sustained gap drains the ring → the watchdog falls back to Opus.
      });
  }

  /** Tear down the graph + WebSocket entirely. Safe to call repeatedly. */
  async disable(): Promise<void> {
    this.gen++; // abort any enable() still in flight
    this.enabled = false;
    this.heard = false;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.watchdog !== null) {
      window.clearInterval(this.watchdog);
      this.watchdog = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
    if (this.flac) {
      try {
        this.flac.free();
      } catch {
        /* ignore */
      }
      this.flac = null;
    }
    if (this.opus) {
      try {
        this.opus.free();
      } catch {
        /* ignore */
      }
      this.opus = null;
    }
    if (this.node) {
      try {
        this.node.disconnect();
      } catch {
        /* ignore */
      }
      this.node = null;
    }
    this.gain = null;
    if (this.ctx) {
      const ctx = this.ctx;
      this.ctx = null;
      try {
        await ctx.close();
      } catch {
        /* ignore */
      }
    }
    this.depthFrames = 0;
  }

  setVolume(v: number): void {
    this.volume = Math.min(1, Math.max(0, v));
    if (this.gain) this.gain.gain.value = this.muted ? 0 : this.volume;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.gain) this.gain.gain.value = m ? 0 : this.volume;
  }

  /** Linearly ramp the output gain to `value` over `ms` (for crossfading with the
   *  Opus path on toggle). */
  private fadeTo(value: number, ms: number): void {
    if (!this.gain || !this.ctx) return;
    const now = this.ctx.currentTime;
    const g = this.gain.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(value, now + Math.max(0.005, ms / 1000));
  }

  /** Fade the lossless output up to the current volume (no-op while muted). */
  fadeIn(ms: number): void {
    this.fadeTo(this.muted ? 0 : this.volume, ms);
  }

  /** Fade the lossless output down to silence (before disable, under the Opus path). */
  fadeOut(ms: number): void {
    this.fadeTo(0, ms);
  }

  /** Route lossless output to a specific device (Chromium: AudioContext.setSinkId). */
  async setOutputDevice(deviceId: string): Promise<boolean> {
    this.sinkId = deviceId;
    return this.applySink(deviceId);
  }

  static supportsOutputSelection(): boolean {
    return (
      typeof AudioContext !== "undefined" &&
      typeof (AudioContext.prototype as unknown as { setSinkId?: unknown }).setSinkId === "function"
    );
  }

  private async applySink(deviceId: string): Promise<boolean> {
    if (this.ctx && typeof this.ctx.setSinkId === "function") {
      try {
        await this.ctx.setSinkId(deviceId);
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }
}
